/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * AckDocUploadModal — Simple file upload modal for EOS acknowledgement document templates.
 * Migrated from: lego/component/document-migration/DocMigration.jsx
 */

import { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useServiceContext } from '../../../root'
import Modal from '../../../components/Modal'

export interface AckDocUploadModalProps {
  onDocUploadSuccess?: () => void
}

const AckDocUploadModal: React.FC<AckDocUploadModalProps> = ({ onDocUploadSuccess }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [isOpen, setIsOpen] = useState(true)
  const [fileToUpload, setFileToUpload] = useState<File | null>(null)

  const doClose = () => {
    setIsOpen(false)
  }

  const handleSubmit = () => {
    if (!fileToUpload) {
      context.service.toast.error('ERROR_YOU_MUST_UPLOAD_FILE')
      return
    }

    const checkDirectRCMConnection = context.service.checkmate.isCheckmated()
      ? context.service.checkmate.initializeConnectionWithDirectRCM()
      : Promise.resolve()

    checkDirectRCMConnection.then(() => {
      context.api.filesUploadApi.uploadAckDocTemplate(fileToUpload, true).then(
        () => {
          context.service.toast.success('BROWSER_UPLOAD_FILE_SUCCESS_MSG')
          onDocUploadSuccess?.()
          doClose()
        },
        (error) => {
          context.service.toast.error(error?.body?.message ?? error?.data?.message ?? 'ERROR')
          doClose()
        }
      )
    })
  }

  const changeHandler = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setFileToUpload(evt?.target?.files?.[0] ?? null)
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('EOS_ADD_ACK_DOC_TEMPLATE')}
      isLevelTwoModal
      className="theme-sandman upload-doc-modal-container level-one-modal react-ack-doc-upload-modal"
      id="modal-add-file"
    >
      <Modal.Body>
        <div className="d-inline-flex">
          <div>
            <label
              className="btn btn-md btn-upload btn-chita btn-second mb20 text-transform-none upload-button"
              htmlFor="file-upload-input"
              title="BrowseFiles"
            >
              {fileToUpload?.name ? fileToUpload.name : translate('BROWSER_BROWSE_FILE')}
              <input type="file" id="file-upload-input" onChange={changeHandler} />
            </label>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button className="btn btn-link" onClick={doClose}>
          {translate('CANCEL')}
        </Button>
        <Button data-action="confirm" className="btn btn-primary text-capitalize" onClick={handleSubmit}>
          {translate('UPLOAD')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AckDocUploadModal
